import { useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useCustomer, useUpdateCustomer, useDeleteCustomer } from '@/features/customers/hooks/useCustomers'
import { useCustomerInvoices, useCreateInvoice } from '@/features/invoices/hooks/useInvoices'
import { useCustomerContactLog, useCreateContactLog, useUpdateContactLog, useDeleteContactLog } from '@/features/contact-log/hooks/useContactLog'
import { useCustomerPaymentPlans, useCreatePaymentPlan, useUpdatePaymentPlan, useDeletePaymentPlan } from '@/features/payment-plans/hooks/usePaymentPlans'
import { usePayments } from '@/features/payments/hooks/usePayments'
import { CustomerForm } from '@/features/customers/components/CustomerForm'
import { InvoiceForm } from '@/features/invoices/components/InvoiceForm'
import { InvoiceDetailModal } from '@/features/invoices/components/InvoiceDetailModal'
import { ContactLogForm } from '@/features/contact-log/components/ContactLogForm'
import { ContactLogDetailModal } from '@/features/contact-log/components/ContactLogDetailModal'
import { PaymentPlanForm } from '@/features/payment-plans/components/PaymentPlanForm'
import { PaymentPlanDetailModal } from '@/features/payment-plans/components/PaymentPlanDetailModal'
import { AgingBadge } from '@/features/invoices/components/AgingBadge'
import { calculateDaysPastDue } from '@/utils/aging'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { Badge } from '@/components/ui/Badge'
import { DataTable } from '@/components/data-table/DataTable'
import { ArrowLeft, Edit, Plus, MessageSquare, Calendar, Trash2 } from 'lucide-react'
import { formatCurrency, formatDate, formatDateTime } from '@/lib/format'
import { enrichInvoicesWithAging } from '@/lib/date-utils'
import { supabase } from '@/lib/supabase'

export function CustomerDetailPage() {
  const { customerId } = useParams<{ customerId: string }>()
  const navigate = useNavigate()
  const { data: customer, isLoading } = useCustomer(customerId)
  const { data: invoices } = useCustomerInvoices(customerId)
  const { data: contactLogs } = useCustomerContactLog(customerId)
  const { data: paymentPlans } = useCustomerPaymentPlans(customerId)
  const { data: allPayments } = usePayments()
  const updateCustomer = useUpdateCustomer()
  const deleteCustomer = useDeleteCustomer()
  const createInvoice = useCreateInvoice()
  const createContactLog = useCreateContactLog()
  const createPaymentPlan = useCreatePaymentPlan()
  const updateContactLog = useUpdateContactLog()
  const deleteContactLog = useDeleteContactLog()
  const updatePaymentPlan = useUpdatePaymentPlan()
  const deletePaymentPlan = useDeletePaymentPlan()
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteConfirmed, setDeleteConfirmed] = useState(false)
  const [reportsAcknowledged, setReportsAcknowledged] = useState(false)
  const [showInvoiceModal, setShowInvoiceModal] = useState(false)
  const [showContactLogModal, setShowContactLogModal] = useState(false)
  const [showPaymentPlanModal, setShowPaymentPlanModal] = useState(false)
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null)
  const [selectedContactLog, setSelectedContactLog] = useState<any>(null)
  const [selectedPaymentPlan, setSelectedPaymentPlan] = useState<any>(null)

  // Calculate total AR balance for this customer - must be before early returns
  const totalARBalance = useMemo(() => {
    if (!invoices) return 0
    return invoices.reduce((sum: number, inv: any) => sum + (inv.balance_due || 0), 0)
  }, [invoices])

  // Enrich invoices with calculated days_past_due - must be before early returns
  const invoicesWithAging = useMemo(() => {
    if (!invoices) return []
    return enrichInvoicesWithAging(invoices)
  }, [invoices])

  // Early returns AFTER all hooks
  if (isLoading) return <div>Loading...</div>
  if (!customer) return <div>Customer not found</div>

  const handleUpdate = async (data: any) => {
    await updateCustomer.mutateAsync({ id: customer.id, updates: data })
    setShowEditModal(false)
  }

  const handleDelete = async () => {
    if (!deleteConfirmed) {
      alert('Please confirm that you want to delete this customer')
      return
    }
    if (!reportsAcknowledged) {
      alert('Please acknowledge that you have saved all necessary data')
      return
    }

    try {
      // Get all invoices for this customer to calculate total AR being removed
      const { data: customerInvoices } = await supabase
        .from('invoices')
        .select('balance_due')
        .eq('customer_id', customer.id)

      const totalARBeingRemoved = (customerInvoices as any)?.reduce(
        (sum: number, inv: any) => sum + (inv.balance_due || 0),
        0
      ) || 0

      console.log(`Customer ${customer.name}: Removing $${totalARBeingRemoved} from baseline AR`)

      // Delete customer (cascade deletes invoices, payments, etc)
      await deleteCustomer.mutateAsync(customer.id)

      // Update client's baseline AR
      if (totalARBeingRemoved > 0) {
        const { data: client } = await supabase
          .from('clients')
          .select('baseline_ar_total')
          .eq('id', customer.client_id)
          .single()

        const currentBaseline = (client as any)?.baseline_ar_total || 0
        const newBaseline = Math.max(0, currentBaseline - totalARBeingRemoved)

        console.log(`Updating client baseline AR: ${currentBaseline} - ${totalARBeingRemoved} = ${newBaseline}`)

        await supabase
          .from('clients')
          .update({ baseline_ar_total: newBaseline } as never)
          .eq('id', customer.client_id)

        console.log(`✓ Updated client baseline AR to ${newBaseline}`)
      }

      navigate(`/clients/${customer.client_id}`)
    } catch (error) {
      console.error('Failed to delete customer:', error)
      alert(`Failed to delete customer: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  const handleCreateInvoice = async (data: any) => {
    await createInvoice.mutateAsync({ ...data, client_id: customer.client_id })
    setShowInvoiceModal(false)
  }

  const handleCreateContactLog = async (data: any) => {
    await createContactLog.mutateAsync({ ...data, customer_id: customer.id, client_id: customer.client_id })
    setShowContactLogModal(false)
  }

  const handleCreatePaymentPlan = async (data: any) => {
    await createPaymentPlan.mutateAsync({ ...data, customer_id: customer.id, client_id: customer.client_id })
    setShowPaymentPlanModal(false)
  }

  const handleUpdateContactLog = async (id: string, updates: any) => {
    await updateContactLog.mutateAsync({ id, updates })
  }

  const handleDeleteContactLog = async (id: string) => {
    try {
      await deleteContactLog.mutateAsync(id)
      setSelectedContactLog(null)
    } catch (error) {
      console.error('Failed to delete contact log:', error)
      alert(`Failed to delete contact log: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  const handleUpdatePaymentPlan = async (id: string, updates: any) => {
    await updatePaymentPlan.mutateAsync({ id, updates })
  }

  const handleDeletePaymentPlan = async (id: string) => {
    await deletePaymentPlan.mutateAsync(id)
    setSelectedPaymentPlan(null)
  }

  const activePaymentPlan = paymentPlans?.find((plan: any) => plan.status === 'active')

  // Get payment counts and last payment dates for each invoice
  const getInvoicePayments = (invoiceId: string) => {
    return allPayments?.filter(p => p.invoice_ids?.includes(invoiceId)) || []
  }

  const invoiceColumns = [
    { key: 'invoice_number', header: 'Invoice #' },
    { key: 'invoice_date', header: 'Date', render: (i: any) => formatDate(i.invoice_date) },
    { key: 'due_date', header: 'Due Date', render: (i: any) => formatDate(i.due_date) },
    { key: 'original_amount', header: 'Amount', render: (i: any) => formatCurrency(i.original_amount) },
    { key: 'balance_due', header: 'Balance', render: (i: any) => formatCurrency(i.balance_due) },
    { 
      key: 'payments_made', 
      header: 'Payments Made', 
      render: (i: any) => {
        const count = getInvoicePayments(i.id).length
        return count > 0 ? <Badge variant="success">{count}</Badge> : <span className="text-gray-400">0</span>
      }
    },
    { 
      key: 'last_payment', 
      header: 'Last Payment', 
      render: (i: any) => {
        const payments = getInvoicePayments(i.id)
        if (payments.length === 0) return <span className="text-gray-400">—</span>
        const lastPayment = payments.sort((a, b) => 
          new Date(b.payment_date).getTime() - new Date(a.payment_date).getTime()
        )[0]
        return <span className="text-sm">{formatDate(lastPayment.payment_date)}</span>
      }
    },
    { key: 'days_past_due', header: 'Aging', render: (i: any) => <AgingBadge daysPastDue={calculateDaysPastDue(i)} /> },
    { key: 'status', header: 'Status', render: (i: any) => <Badge>{i.status}</Badge> },
  ]

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" onClick={() => navigate(-1)}><ArrowLeft className="h-4 w-4" /></Button>
          <div>
            <h1 className="text-2xl font-bold">{customer.name}</h1>
            <p className="text-sm text-gray-600">Customer Details</p>
          </div>
        </div>
        <div className="flex space-x-3">
          <Button variant="secondary" onClick={() => setShowEditModal(true)}>
            <Edit className="h-4 w-4 mr-2" />Edit
          </Button>
          <Button variant="danger" onClick={() => setShowDeleteConfirm(true)}>
            <Trash2 className="h-4 w-4 mr-2" />Delete
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <Card>
          <h2 className="text-lg font-semibold mb-4">Contact Information</h2>
          <dl className="space-y-2">
            <div><dt className="text-sm text-gray-500">Contact Person</dt><dd className="text-sm">{customer.contact_person || '—'}</dd></div>
            <div><dt className="text-sm text-gray-500">Phone</dt><dd className="text-sm">{customer.phone || '—'}</dd></div>
            <div><dt className="text-sm text-gray-500">Email</dt><dd className="text-sm">{customer.email || '—'}</dd></div>
            <div><dt className="text-sm text-gray-500">Address</dt><dd className="text-sm">{customer.address || '—'}</dd></div>
          </dl>
        </Card>
        <Card>
          <h2 className="text-lg font-semibold mb-4">Total AR Balance</h2>
          <p className="text-3xl font-bold text-primary-600">{formatCurrency(totalARBalance)}</p>
          <p className="text-sm text-gray-500 mt-2">
            {invoices?.length || 0} invoice{invoices?.length !== 1 ? 's' : ''}
          </p>
        </Card>
        <Card>
          <h2 className="text-lg font-semibold mb-4">Notes</h2>
          <p className="text-sm text-gray-700">{customer.notes || 'No notes'}</p>
        </Card>
      </div>

      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Invoices</h2>
          <Button onClick={() => setShowInvoiceModal(true)}>
            <Plus className="h-4 w-4 mr-2" />Add Invoice
          </Button>
        </div>
        <DataTable
          data={invoicesWithAging}
          columns={invoiceColumns}
          onRowClick={(invoice) => setSelectedInvoice(invoice)}
          emptyMessage="No invoices yet"
        />
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center">
              <MessageSquare className="h-5 w-5 mr-2" />Contact History
            </h2>
            <Button onClick={() => setShowContactLogModal(true)}>
              <Plus className="h-4 w-4 mr-2" />Add Contact
            </Button>
          </div>
          {contactLogs && contactLogs.length > 0 ? (
            <div className="space-y-3">
              {contactLogs.slice(0, 5).map((log: any) => (
                <div
                  key={log.id}
                  onClick={() => setSelectedContactLog(log)}
                  className="border-b pb-3 last:border-b-0 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <Badge variant="default">{log.contact_method}</Badge>
                        <span className="text-sm text-gray-500">{formatDateTime(log.contact_date)}</span>
                      </div>
                      <p className="text-sm font-medium">{log.person_contacted}</p>
                      <p className="text-sm text-gray-700 mt-1">{log.summary}</p>
                      {log.outcome && (
                        <p className="text-sm text-gray-600 mt-1">
                          <span className="font-medium">Outcome:</span> {log.outcome}
                        </p>
                      )}
                      {log.next_action && (
                        <p className="text-sm text-gray-600 mt-1">
                          <span className="font-medium">Next Action:</span> {log.next_action}
                        </p>
                      )}
                      {log.followup_date && (
                        <p className="text-sm text-gray-600 mt-1">
                          <span className="font-medium">Follow-up:</span> {formatDate(log.followup_date)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No contact history yet</p>
          )}
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center">
              <Calendar className="h-5 w-5 mr-2" />Payment Plan
            </h2>
            {!activePaymentPlan && (
              <Button onClick={() => setShowPaymentPlanModal(true)}>
                <Plus className="h-4 w-4 mr-2" />Create Plan
              </Button>
            )}
          </div>
          {activePaymentPlan ? (
            <div
              onClick={() => setSelectedPaymentPlan(activePaymentPlan)}
              className="space-y-3 cursor-pointer hover:bg-gray-50 p-3 rounded transition-colors"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Status</span>
                <Badge variant="success">{activePaymentPlan.status}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Total Amount</span>
                <span className="text-sm font-semibold">{formatCurrency(activePaymentPlan.total_amount)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Installment</span>
                <span className="text-sm font-semibold">
                  {formatCurrency(activePaymentPlan.installment_amount)} / {activePaymentPlan.frequency}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Start Date</span>
                <span className="text-sm">{formatDate(activePaymentPlan.start_date)}</span>
              </div>
              {activePaymentPlan.end_date && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">End Date</span>
                  <span className="text-sm">{formatDate(activePaymentPlan.end_date)}</span>
                </div>
              )}
              {activePaymentPlan.notes && (
                <div className="mt-3 pt-3 border-t">
                  <p className="text-sm text-gray-600 font-medium mb-1">Notes</p>
                  <p className="text-sm text-gray-700">{activePaymentPlan.notes}</p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No active payment plan</p>
          )}
        </Card>
      </div>

      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title="Edit Customer" size="lg">
        <CustomerForm initialData={customer} onSubmit={handleUpdate} onCancel={() => setShowEditModal(false)} loading={updateCustomer.isPending} />
      </Modal>

      <Modal isOpen={showInvoiceModal} onClose={() => setShowInvoiceModal(false)} title="Add Invoice">
        <InvoiceForm customerId={customer.id} onSubmit={handleCreateInvoice} onCancel={() => setShowInvoiceModal(false)} loading={createInvoice.isPending} />
      </Modal>

      <Modal isOpen={showContactLogModal} onClose={() => setShowContactLogModal(false)} title="Log Contact" size="lg">
        <ContactLogForm onSubmit={handleCreateContactLog} onCancel={() => setShowContactLogModal(false)} loading={createContactLog.isPending} />
      </Modal>

      <Modal isOpen={showPaymentPlanModal} onClose={() => setShowPaymentPlanModal(false)} title="Create Payment Plan" size="lg">
        <PaymentPlanForm onSubmit={handleCreatePaymentPlan} onCancel={() => setShowPaymentPlanModal(false)} loading={createPaymentPlan.isPending} />
      </Modal>

      <Modal isOpen={showDeleteConfirm} onClose={() => {
        setShowDeleteConfirm(false)
        setDeleteConfirmed(false)
        setReportsAcknowledged(false)
      }} title="Delete Customer">
        <div>
          <p className="text-gray-700 mb-6">
            You are about to delete <strong>{customer.name}</strong>. This action cannot be undone and will permanently delete:
          </p>
          <ul className="list-disc list-inside text-gray-700 mb-6 space-y-1">
            <li>All invoices for this customer</li>
            <li>All payment records for this customer</li>
            <li>All contact logs for this customer</li>
            <li>All payment plans for this customer</li>
          </ul>

          <div className="space-y-4 mb-6">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={deleteConfirmed}
                onChange={(e) => setDeleteConfirmed(e.target.checked)}
                className="mt-1 h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
              />
              <span className="text-sm text-gray-700">
                <strong className="text-red-600">* Required:</strong> I understand this will permanently delete all data for {customer.name} and this action cannot be undone.
              </span>
            </label>

            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={reportsAcknowledged}
                onChange={(e) => setReportsAcknowledged(e.target.checked)}
                className="mt-1 h-4 w-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
              />
              <span className="text-sm text-gray-700">
                <strong className="text-amber-600">* Required:</strong> I have saved all necessary invoices and payment records for this customer.
              </span>
            </label>
          </div>

          <div className="flex justify-end space-x-3">
            <Button variant="secondary" onClick={() => {
              setShowDeleteConfirm(false)
              setDeleteConfirmed(false)
              setReportsAcknowledged(false)
            }}>Cancel</Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              loading={deleteCustomer.isPending}
              disabled={!deleteConfirmed || !reportsAcknowledged}
            >
              Delete Customer
            </Button>
          </div>
        </div>
      </Modal>

      {selectedInvoice && (
        <InvoiceDetailModal
          invoice={selectedInvoice}
          payments={allPayments || []}
          isOpen={!!selectedInvoice}
          onClose={() => setSelectedInvoice(null)}
        />
      )}

      {selectedContactLog && (
        <ContactLogDetailModal
          contactLog={selectedContactLog}
          isOpen={!!selectedContactLog}
          onClose={() => setSelectedContactLog(null)}
          onUpdate={handleUpdateContactLog}
          onDelete={handleDeleteContactLog}
          updating={updateContactLog.isPending}
          deleting={deleteContactLog.isPending}
        />
      )}

      {selectedPaymentPlan && (
        <PaymentPlanDetailModal
          paymentPlan={selectedPaymentPlan}
          isOpen={!!selectedPaymentPlan}
          onClose={() => setSelectedPaymentPlan(null)}
          onUpdate={handleUpdatePaymentPlan}
          onDelete={handleDeletePaymentPlan}
          updating={updatePaymentPlan.isPending}
          deleting={deletePaymentPlan.isPending}
        />
      )}
    </div>
  )
}
